import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  Firestore,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  type FirestoreDataConverter,
  type QuerySnapshot,
  type Unsubscribe,
} from 'firebase/firestore';
import {
  assertGroup,
  assertGroupMember,
  createSlug,
  groupMemberSchema,
  groupWriteSchema,
  memberStatusSchema,
  type Group,
  type GroupMember,
  type GroupWrite,
  nowIsoString,
} from '@penticton-dev-events/shared';
import { getUserProfile } from '../../auth/api/userRepository';

const groupsCollection = (firestore: Firestore) =>
  collection(firestore, 'groups').withConverter(groupConverter);

const groupMembersCollection = (firestore: Firestore, groupId: string) =>
  collection(firestore, `groups/${groupId}/members`).withConverter(groupMemberConverter);

type GroupDocument = Omit<Group, 'id'>;
type GroupMemberDocument = GroupMember;

const groupConverter: FirestoreDataConverter<Group, GroupDocument> = {
  toFirestore(group: Group): GroupDocument {
    const { id: ignoredId, summary, ...rest } = group;
    void ignoredId;

    return summary === undefined ? rest : { ...rest, summary };
  },
  fromFirestore(snapshot): Group {
    const payload = snapshot.data();

    // Clean up null values - convert them to undefined
    // This handles existing data where fields were set to null instead of deleted
    const cleanedPayload: any = { id: snapshot.id };
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== null) {
        cleanedPayload[key] = value;
      }
      // Skip null values - they become undefined
    });

    return assertGroup(cleanedPayload);
  },
};

const groupMemberConverter: FirestoreDataConverter<GroupMember, GroupMemberDocument> = {
  toFirestore(data: GroupMember): GroupMemberDocument {
    const parsed = groupMemberSchema.parse(data);
    // Remove undefined values - Firestore doesn't accept them
    const cleaned: Record<string, unknown> = {};
    Object.entries(parsed).forEach(([key, value]) => {
      if (value !== undefined) {
        cleaned[key] = value;
      }
    });
    return cleaned as GroupMemberDocument;
  },
  fromFirestore(snapshot): GroupMember {
    const payload = snapshot.data();
    return assertGroupMember(payload);
  },
};

export const subscribeToGroups = (
  firestore: Firestore,
  handleSnapshot: (groups: Group[]) => void,
  handleError: (error: Error) => void,
): Unsubscribe => {
  const groupsQuery = query(groupsCollection(firestore), orderBy('createdAt', 'desc'));

  return onSnapshot(
    groupsQuery,
    (snapshot: QuerySnapshot<Group>) => {
      handleSnapshot(snapshot.docs.map(docSnapshot => docSnapshot.data()));
    },
    error => {
      handleError(error);
    },
  );
};

export const listGroupsOnce = async (firestore: Firestore): Promise<Group[]> => {
  const snapshot = await getDocs(
    query(groupsCollection(firestore), orderBy('createdAt')),
  );
  return snapshot.docs.map(docSnapshot => docSnapshot.data());
};

export const createGroup = async (
  firestore: Firestore,
  payload: Omit<GroupWrite, 'id'> & { readonly organizerUid: string },
): Promise<Group> => {
  const trimmedName = payload.name.trim();
  const providedSlug = payload.slug?.trim();
  const slugSeed = providedSlug && providedSlug.length > 0 ? providedSlug : trimmedName;
  const slug = createSlug(slugSeed);

  if (!slug) {
    throw new Error(
      'Unable to generate a slug for this group. Please adjust the name or slug.',
    );
  }

  const parsed = groupWriteSchema.parse({
    name: trimmedName,
    slug,
    organizerUid: payload.organizerUid,
    organizerName: payload.organizerName,
    autoAcceptMembers: payload.autoAcceptMembers,
    approvalStatus: payload.approvalStatus,
    approvedBy: payload.approvedBy,
    approvedAt: payload.approvedAt,
    createdAt: payload.createdAt,
    summary: payload.summary?.trim() ? payload.summary.trim() : undefined,
  });

  const id = parsed.slug;
  const document = doc(groupsCollection(firestore), id);

  // Check if a group with this slug already exists
  const existingDoc = await getDoc(document);
  if (existingDoc.exists()) {
    throw new Error(
      `A group with the name "${trimmedName}" already exists. Please choose a different name.`,
    );
  }

  const group: Group = {
    ...parsed,
    id,
    createdAt: parsed.createdAt ?? nowIsoString(),
    approvalStatus: parsed.approvalStatus ?? 'approved', // Default to approved if not specified
    memberCount: 1, // Start with 1 member (the organizer)
  };

  // Create the group document
  await setDoc(document, groupConverter.toFirestore(group));

  // Fetch organizer profile to get email and photoURL for denormalized member data
  const organizerProfile = await getUserProfile(firestore, payload.organizerUid);

  if (!organizerProfile) {
    throw new Error(`Organizer profile not found for UID: ${payload.organizerUid}`);
  }

  // Automatically add the organizer as a member with organizer role
  const membersCollection = groupMembersCollection(firestore, id);
  const organizerMemberDoc = doc(membersCollection, payload.organizerUid);

  const organizerMemberData: Record<string, unknown> = {
    uid: payload.organizerUid,
    role: 'organizer',
    status: 'active', // Organizer is automatically active
    joinedAt: group.createdAt,
    displayName: organizerProfile.displayName,
    email: organizerProfile.email,
  };

  // Only add photoURL if it exists
  if (organizerProfile.photoURL) {
    organizerMemberData.photoURL = organizerProfile.photoURL;
  }

  const organizerMember: GroupMember = assertGroupMember(organizerMemberData);

  await setDoc(organizerMemberDoc, groupMemberConverter.toFirestore(organizerMember));

  console.log(
    `✅ Created group "${group.name}" with organizer ${payload.organizerUid} as member`,
  );

  return group;
};

export const requestMembership = async (
  firestore: Firestore,
  group: Group,
  member: Pick<GroupMember, 'uid' | 'role' | 'displayName' | 'email'> & {
    photoURL?: string | null;
  },
): Promise<GroupMember> => {
  const members = groupMembersCollection(firestore, group.id);
  const memberDoc = doc(members, member.uid);

  // Build payload without undefined values
  const memberData: Record<string, unknown> = {
    uid: member.uid,
    role: member.role,
    status: memberStatusSchema.parse(group.autoAcceptMembers ? 'active' : 'pending'),
    joinedAt: nowIsoString(),
    displayName: member.displayName,
    email: member.email,
  };

  // Only add photoURL if it's not null/undefined
  if (member.photoURL) {
    memberData.photoURL = member.photoURL;
  }

  const payload: GroupMember = assertGroupMember(memberData);

  await setDoc(memberDoc, groupMemberConverter.toFirestore(payload));

  return payload;
};

export const approveMembership = async (
  firestore: Firestore,
  groupId: string,
  memberId: string,
): Promise<void> => {
  const members = groupMembersCollection(firestore, groupId);
  const memberDoc = doc(members, memberId);

  await updateDoc(memberDoc, { status: memberStatusSchema.parse('active') });
};

export const denyMembership = async (
  firestore: Firestore,
  groupId: string,
  memberId: string,
): Promise<void> => {
  const members = groupMembersCollection(firestore, groupId);
  const memberDoc = doc(members, memberId);

  await deleteDoc(memberDoc);
};

export const leaveMembership = async (
  firestore: Firestore,
  groupId: string,
  memberId: string,
): Promise<void> => {
  const members = groupMembersCollection(firestore, groupId);
  const memberDoc = doc(members, memberId);

  await deleteDoc(memberDoc);
};

export const subscribeToGroupMembers = (
  firestore: Firestore,
  groupId: string,
  handleSnapshot: (members: GroupMember[]) => void,
  handleError: (error: Error) => void,
): Unsubscribe => {
  const membersRef = groupMembersCollection(firestore, groupId);

  return onSnapshot(
    membersRef,
    snapshot => {
      const members = snapshot.docs.map(docSnapshot =>
        assertGroupMember(docSnapshot.data()),
      );
      handleSnapshot(members);
    },
    handleError,
  );
};

export const getGroupById = async (
  firestore: Firestore,
  groupId: string,
): Promise<Group | null> => {
  const groupDoc = doc(groupsCollection(firestore), groupId);
  const snapshot = await getDoc(groupDoc);

  if (!snapshot.exists()) {
    return null;
  }

  return snapshot.data();
};

export const updateGroupSettings = async (
  firestore: Firestore,
  groupId: string,
  settings: Partial<GroupWrite>,
): Promise<void> => {
  const groupDoc = doc(groupsCollection(firestore), groupId);
  const updateData: Record<string, unknown> = {};

  // Only update fields that are provided
  if (settings.name !== undefined) updateData.name = settings.name;
  if (settings.summary !== undefined) updateData.summary = settings.summary;
  if (settings.autoAcceptMembers !== undefined)
    updateData.autoAcceptMembers = settings.autoAcceptMembers;
  if (settings.organizerUid !== undefined)
    updateData.organizerUid = settings.organizerUid;
  if (settings.organizerName !== undefined)
    updateData.organizerName = settings.organizerName;

  await updateDoc(groupDoc, updateData as any);
};

export const deleteGroup = async (
  firestore: Firestore,
  groupId: string,
): Promise<void> => {
  const groupDoc = doc(groupsCollection(firestore), groupId);

  // Delete all members subcollection first
  const membersSnapshot = await getDocs(groupMembersCollection(firestore, groupId));
  const memberDeletePromises = membersSnapshot.docs.map(memberDoc =>
    deleteDoc(memberDoc.ref),
  );
  await Promise.all(memberDeletePromises);

  // Delete the group document
  await deleteDoc(groupDoc);
};

/**
 * Update group poster information
 * Only canonical organizers and admins can update poster fields (enforced by Firestore rules)
 */
export const updateGroupPoster = async (
  firestore: Firestore,
  groupId: string,
  poster: {
    posterUrl?: string;
    posterStoragePath?: string;
    posterAlt?: string;
  },
): Promise<void> => {
  console.log('🔧 updateGroupPoster called with:', { groupId, poster });

  // Use raw doc reference without converter for partial updates
  const groupDocRef = doc(collection(firestore, 'groups'), groupId);
  const updateData: Record<string, unknown> = {};

  if (poster.posterUrl !== undefined) updateData.posterUrl = poster.posterUrl;
  if (poster.posterStoragePath !== undefined) updateData.posterStoragePath = poster.posterStoragePath;
  if (poster.posterAlt !== undefined) updateData.posterAlt = poster.posterAlt;

  console.log('🔧 Firestore updateDoc payload:', updateData);

  try {
    await updateDoc(groupDocRef, updateData);
    console.log('✅ Firestore updateDoc succeeded');
  } catch (error) {
    console.error('❌ Firestore updateDoc failed:', error);
    throw error;
  }
};

/**
 * Remove group poster
 * Only canonical organizers and admins can remove posters (enforced by Firestore rules)
 */
export const removeGroupPoster = async (
  firestore: Firestore,
  groupId: string,
): Promise<void> => {
  console.log('🗑️ removeGroupPoster called for groupId:', groupId);

  // Use raw doc reference without converter for partial updates
  const groupDocRef = doc(collection(firestore, 'groups'), groupId);

  // Use deleteField() to remove fields instead of setting to null
  // This prevents Zod validation errors since optional fields expect undefined, not null
  await updateDoc(groupDocRef, {
    posterUrl: deleteField(),
    posterStoragePath: deleteField(),
    posterAlt: deleteField(),
  });

  console.log('✅ Poster fields deleted from Firestore');
};
